import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import * as THREE from 'three';
import { MindARThree } from 'mind-ar/dist/mindar-face-three.prod.js';
import { FilterAndCaptureButtons } from '../../utils/screenCapture';
import { makeEarlobeCompute, EARLOBE_DEFAULTS } from '../../utils/earlobeAnchor';
import { useParams } from 'react-router-dom';
import './Earrings.css';
import config from '../../config/config';

// Fallback when manifest provides no physical dimensions or category:
// 0.30 of ear-to-ear width ≈ 45mm on a 150mm face, a typical medium drop/jhumka.
const EARRING_SCALE_FACTOR = 0.30;

// Real-world ear-to-ear distance in mm. In MindAR canonical space 1 Three.js unit ≈ 1 cm,
// so the inter-earlobe anchor distance (~15.5 units) ≈ 155 mm on an adult face.
// Using 150 as a round conservative value; tune if empirical testing shows drift.
const FACE_REFERENCE_MM = 150;

const SIZE_PRESETS = {
  stud:       { heightMm: 10 },
  hoopSmall:  { heightMm: 25 },
  dropSmall:  { heightMm: 32 },
  dropMedium: { heightMm: 48 },
  chandelier: { heightMm: 65 },
};

// Safety clamps expressed as a fraction of smoothed faceWidth
const CATEGORY_LIMITS = {
  stud:       { min: 0.05, max: 0.18 },
  hoopSmall:  { min: 0.12, max: 0.30 },
  dropSmall:  { min: 0.15, max: 0.40 },
  dropMedium: { min: 0.25, max: 0.60 },
  chandelier: { min: 0.35, max: 0.85 },
};
const DEFAULT_LIMITS = { min: 0.05, max: 0.85 };

const getManifestNumber = (manifest, field, fallback) => {
  const val = Number(manifest?.[field]);
  return Number.isFinite(val) ? val : fallback;
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// defaultScale is a vendor correction multiplier on top of the physical size (1.0 = no correction).
// Priority: physicalHeightMm → category preset → global default; defaultScale applied to all paths.
const resolveScaleFactor = (manifest) => {
  const ds = Number(manifest?.defaultScale);
  const multiplier = Number.isFinite(ds) && ds > 0 ? ds : 1.0;
  const heightMm = Number(manifest?.physicalHeightMm);
  if (Number.isFinite(heightMm) && heightMm > 0) return (heightMm / FACE_REFERENCE_MM) * multiplier;
  const preset = SIZE_PRESETS[manifest?.category];
  if (preset) return (preset.heightMm / FACE_REFERENCE_MM) * multiplier;
  return EARRING_SCALE_FACTOR * multiplier;
};

const hasPhysicalHeight = (manifest) => {
  const heightMm = Number(manifest?.physicalHeightMm);
  return Number.isFinite(heightMm) && heightMm > 0;
};

const resolveLimits = (manifest) => {
  const limits = CATEGORY_LIMITS[manifest?.category] ?? DEFAULT_LIMITS;
  return hasPhysicalHeight(manifest) ? { ...limits, min: 0 } : limits;
};

function Earrings() {
  const { sku } = useParams();
  const category = 'earrings';
  const containerRef = useRef(null);
  const mindarThreeRef = useRef(null);
  const sceneRef = useRef(null);
  const anchorRef = useRef({
    leftEarAnchor: null,
    rightEarAnchor: null,
  });
  const modelsRef = useRef({
    faceMeshOccluder: null,
    leftEarring: null,
    rightEarring: null
  });
  const rendererRef = useRef(null);
  const [selectedFilter, setSelectedFilter] = useState('none');
  const [error, setError] = useState(null);

  // Cartoon shader
  const cartoonMaterial = useMemo(() => new THREE.ShaderMaterial({
    uniforms: {
      lightDirection: { value: new THREE.Vector3(0, 1, 1).normalize() },
      toonLevel: { value: 4.0 },
      edge: { value: 0.4 }
    },
    vertexShader: `
      varying vec3 vNormal;
      varying vec3 vPosition;

      void main() {
        vNormal = normalize(normalMatrix * normal);
        vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 lightDirection;
      uniform float toonLevel;
      uniform float edge;

      varying vec3 vNormal;
      varying vec3 vPosition;

      void main() {
        float intensity = dot(vNormal, lightDirection);
        intensity = ceil(intensity * toonLevel) / toonLevel;

        // Edge detection
        float edgeIntensity = length(fwidth(vNormal));
        float isEdge = edgeIntensity > edge ? 0.0 : 1.0;

        vec3 toonColor = vec3(intensity * isEdge);
        gl_FragColor = vec4(toonColor, 0.5);
      }
    `,
    transparent: true,
    side: THREE.DoubleSide
  }), []);

  // Load and apply face texture
  const texture = useMemo(() => new THREE.TextureLoader().load('/assets/thumbnails/face-mask.png'), []);

  useEffect(() => {
    if (sceneRef.current && mindarThreeRef.current) {
      const mindarThree = mindarThreeRef.current;
      const { renderer, scene, camera } = sceneRef.current;

      const existingFaceMesh = scene.children.find(child => child.isFaceMesh);
      if (existingFaceMesh) {
        scene.remove(existingFaceMesh);
      }

      if (selectedFilter === 'none') {
        renderer.render(scene, camera);
      } else if (selectedFilter === 'cartoon') {
        const faceMesh = mindarThree.addFaceMesh();
        faceMesh.material = cartoonMaterial;
        faceMesh.isFaceMesh = true;
        scene.add(faceMesh);
        renderer.render(scene, camera);
      } else if (selectedFilter === 'animal') {
        const faceMesh = mindarThree.addFaceMesh();
        faceMesh.material.map = texture;
        faceMesh.material.transparent = true;
        faceMesh.material.needsUpdate = true;
        faceMesh.isFaceMesh = true;
        scene.add(faceMesh);
        renderer.render(scene, camera);
      }
    }
  }, [selectedFilter, cartoonMaterial, texture]);

  const initializeScene = (mindarThree) => {
    const { renderer, scene, camera } = mindarThree;

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.75);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(0, 0, 1);
    camera.add(directionalLight);
    scene.add(camera);

    const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0xbbbbff, 1);
    scene.add(hemisphereLight);

    return { renderer, scene, camera };
  };

  const fetchManifest = useCallback(async () => {
    try {
      const res = await fetch(`${config.cdnUrl}/models/2D/${category}/${sku}/manifest.json`);
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }, [sku]);

  const initializeModels = useCallback(async (mindarThree, scene) => {
    const manifest = await fetchManifest();
    console.log('[2D Earrings] manifest:', JSON.stringify(manifest, null, 2));
    console.log('[2D Earrings] resolveScaleFactor ->', resolveScaleFactor(manifest));
    console.log('[2D Earrings] resolveLimits ->', resolveLimits(manifest));

    const earlobeOffsets = {
      xOffset: getManifestNumber(manifest, 'earlobeXOffset', EARLOBE_DEFAULTS.xOffset),
      yOffset: getManifestNumber(manifest, 'earlobeYOffset', EARLOBE_DEFAULTS.yOffset),
      zDelta:  getManifestNumber(manifest, 'earlobeZDelta',  EARLOBE_DEFAULTS.zDelta),
    };

    anchorRef.current.leftEarAnchor  = mindarThree.addDynamicAnchor(makeEarlobeCompute('left', earlobeOffsets));
    anchorRef.current.rightEarAnchor = mindarThree.addDynamicAnchor(makeEarlobeCompute('right', earlobeOffsets));

    const faceMeshOccluder = mindarThree.addFaceMesh();
    faceMeshOccluder.material = new THREE.MeshBasicMaterial({
      colorWrite: false,
      depthWrite: true,
      depthTest: true,
      side: THREE.DoubleSide,
    });
    faceMeshOccluder.renderOrder = -1;
    scene.add(faceMeshOccluder);
    modelsRef.current.faceMeshOccluder = faceMeshOccluder;

    const textureLoader = new THREE.TextureLoader();
    const loadTexture = (url) => {
      return new Promise((resolve, reject) => {
        textureLoader.load(url,
          (loadedTexture) => {
            loadedTexture.colorSpace = THREE.SRGBColorSpace;
            loadedTexture.encoding = THREE.sRGBEncoding;
            loadedTexture.minFilter = THREE.LinearFilter;
            loadedTexture.magFilter = THREE.LinearFilter;
            resolve(loadedTexture);
          },
          undefined,
          (error) => {
            console.warn(`Failed to load texture from ${url}:`, error);
            reject(error);
          }
        );
      });
    };

    const textureUrl = `${config.cdnUrl}/models/2D/${category}/${sku}/texture.png`;
    const loadedTexture = await loadTexture(textureUrl).catch(() => {
      console.log('Failed to load SKU-specific texture, falling back to default');
      return loadTexture('/assets/2D/earrings/default.png');
    });

    const leftOffset  = manifest?.offsets?.left  ?? { x: 0, y: 0, z: 0.02 };
    const rightOffset = manifest?.offsets?.right ?? { x: 0, y: 0, z: 0.02 };
    const scaleFactor = resolveScaleFactor(manifest);
    const limits      = resolveLimits(manifest);
    const attachX     = getManifestNumber(manifest, 'anchorX', 0.5);
    const attachY     = getManifestNumber(manifest, 'anchorY', 0.08);
    const leftRotDeg  = getManifestNumber(manifest, 'leftRotationOffset', 0);
    const rightRotDeg = getManifestNumber(manifest, 'rightRotationOffset', 0);

    const width = loadedTexture.image.width;
    const height = loadedTexture.image.height;
    const aspectRatio = width / height;

    const material = new THREE.MeshBasicMaterial({
      map: loadedTexture,
      transparent: true,
      opacity: 1.0,
      depthTest: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      alphaTest: 0.1,
    });

    if (modelsRef.current.leftEarring) {
      anchorRef.current.leftEarAnchor.group.remove(modelsRef.current.leftEarring);
      modelsRef.current.leftEarring.geometry.dispose();
      modelsRef.current.leftEarring.material.dispose();
      modelsRef.current.leftEarring = null;
    }
    if (modelsRef.current.rightEarring) {
      anchorRef.current.rightEarAnchor.group.remove(modelsRef.current.rightEarring);
      modelsRef.current.rightEarring.geometry.dispose();
      modelsRef.current.rightEarring.material.dispose();
      modelsRef.current.rightEarring = null;
    }

    // flipX mirrors the attachment point for the right ear; the animation loop
    // also applies scale.x < 0 so the texture itself is mirrored.
    const makeGeometry = (flipX) => {
      const geo = new THREE.PlaneGeometry(aspectRatio, 1);
      const ax = flipX ? 1 - attachX : attachX;
      geo.translate(-(ax - 0.5) * aspectRatio, -(0.5 - attachY), 0);
      return geo;
    };

    const leftPlane = new THREE.Mesh(makeGeometry(false), material.clone());
    leftPlane.position.set(leftOffset.x, leftOffset.y, leftOffset.z);
    leftPlane.rotation.z = (leftRotDeg * Math.PI) / 180;
    leftPlane.userData.scaleFactor = scaleFactor;
    leftPlane.userData.limitsMin   = limits.min;
    leftPlane.userData.limitsMax   = limits.max;
    anchorRef.current.leftEarAnchor.group.add(leftPlane);
    modelsRef.current.leftEarring = leftPlane;

    const rightPlane = new THREE.Mesh(makeGeometry(true), material.clone());
    rightPlane.position.set(rightOffset.x, rightOffset.y, rightOffset.z);
    rightPlane.rotation.z = (rightRotDeg * Math.PI) / 180;
    rightPlane.userData.scaleFactor = scaleFactor;
    rightPlane.userData.limitsMin   = limits.min;
    rightPlane.userData.limitsMax   = limits.max;
    anchorRef.current.rightEarAnchor.group.add(rightPlane);
    modelsRef.current.rightEarring = rightPlane;

  }, [sku, fetchManifest]);

  useEffect(() => {
    let mounted = true;
    const currentMindarRef = mindarThreeRef;
    const currentSceneRef = sceneRef;
    const currentModelsRef = modelsRef.current;

    const setup = async () => {
      if (!mounted) return;

      const mindarThree = new MindARThree({
        container: containerRef.current,
        filterMinCF: 0.001,
        filterBeta: 1,
      });

      const { renderer, scene, camera } = initializeScene(mindarThree);
      rendererRef.current = renderer;
      await initializeModels(mindarThree, scene);

      if (mounted) {
        currentMindarRef.current = mindarThree;
        currentSceneRef.current = { renderer, scene, camera };

        await mindarThree.start();

        const leftPos = new THREE.Vector3();
        const rightPos = new THREE.Vector3();
        const anchorCol = new THREE.Vector3();
        let smoothedFaceWidth = 0;
        let debugLogged = false;

        renderer.setAnimationLoop(() => {
          const { leftEarAnchor, rightEarAnchor } = anchorRef.current;
          const { leftEarring, rightEarring } = modelsRef.current;

          if (leftEarring && rightEarring && leftEarAnchor && rightEarAnchor) {
            leftEarAnchor.group.getWorldPosition(leftPos);
            rightEarAnchor.group.getWorldPosition(rightPos);
            const rawFaceWidth = leftPos.distanceTo(rightPos);

            if (rawFaceWidth > 0) {
              // EMA smoothing to suppress per-frame jitter; cold-start on first valid sample
              smoothedFaceWidth = smoothedFaceWidth === 0
                ? rawFaceWidth
                : smoothedFaceWidth * 0.85 + rawFaceWidth * 0.15;

              const sf   = leftEarring.userData.scaleFactor ?? EARRING_SCALE_FACTOR;
              const lMin = leftEarring.userData.limitsMin   ?? DEFAULT_LIMITS.min;
              const lMax = leftEarring.userData.limitsMax   ?? DEFAULT_LIMITS.max;
              const leftZm  = leftEarring.userData.zoomMultiplier  ?? 1;
              const rightZm = rightEarring.userData.zoomMultiplier ?? 1;

              // MindAR bakes faceScale (metric face width, ~14) into the anchor
              // matrix, so children render faceScale× their local scale. Divide it
              // out so the computed size lands in world units (1 unit ≈ 1 cm).
              const anchorScale = anchorCol.setFromMatrixColumn(leftEarAnchor.group.matrix, 0).length() || 1;
              const leftS  = clamp(smoothedFaceWidth * sf * leftZm,  smoothedFaceWidth * lMin, smoothedFaceWidth * lMax) / anchorScale;
              const rightS = clamp(smoothedFaceWidth * sf * rightZm, smoothedFaceWidth * lMin, smoothedFaceWidth * lMax) / anchorScale;

              if (!debugLogged && smoothedFaceWidth > 0) {
                console.log('[2D Earrings loop] smoothedFaceWidth:', smoothedFaceWidth.toFixed(4));
                console.log('[2D Earrings loop] scaleFactor (sf):', sf.toFixed(4));
                console.log('[2D Earrings loop] anchorScale (baked faceScale):', anchorScale.toFixed(4));
                console.log('[2D Earrings loop] leftS (final scale):', leftS.toFixed(4));
                console.log('[2D Earrings loop] leftPos:', leftPos.toArray().map(v => v.toFixed(3)));
                console.log('[2D Earrings loop] rightPos:', rightPos.toArray().map(v => v.toFixed(3)));
                debugLogged = true;
              }

              // Negative x on the right earring mirrors the texture horizontally
              leftEarring.scale.set(leftS, leftS, leftS);
              rightEarring.scale.set(-rightS, rightS, rightS);

              if (leftEarAnchor.group.visible) {
                const leftZ  = leftEarAnchor.group.matrix.elements[14];
                const rightZ = rightEarAnchor.group.matrix.elements[14];
                const zDiff = leftZ - rightZ;
                const leftOpacity  = clamp(1 + zDiff / (smoothedFaceWidth * 0.6), 0, 1);
                const rightOpacity = clamp(1 - zDiff / (smoothedFaceWidth * 0.6), 0, 1);
                leftEarring.material.opacity  = leftOpacity;
                rightEarring.material.opacity = rightOpacity;
                leftEarring.visible  = leftOpacity  > 0.03;
                rightEarring.visible = rightOpacity > 0.03;
              }
            }
          }

          renderer.render(scene, camera);
        });
      }
    };

    setup();

    return () => {
      mounted = false;
      if (currentMindarRef.current) {
        currentMindarRef.current.stop();
        currentMindarRef.current.renderer.setAnimationLoop(null);

        if (currentSceneRef.current) {
          const { scene, renderer } = currentSceneRef.current;

          Object.values(currentModelsRef).forEach(model => {
            if (model && model.parent) {
              model.parent.remove(model);
            }
          });

          renderer.render(scene, currentSceneRef.current.camera);
          renderer.clear();
        }
      }
    };
  }, [initializeModels]);

  const getRendererInfo = () => {
    return {
      mindarThree: mindarThreeRef.current,
      models: modelsRef.current
    };
  };

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">
          {error}
          <button
            onClick={() => {
              setError(null);
              window.location.reload();
            }}
            className="retry-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="ar-container" ref={containerRef}>
        <video className="video"></video>
      </div>
      <FilterAndCaptureButtons
        getRendererInfo={getRendererInfo}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        mode="earrings"
      />
    </div>
  );
}

export default Earrings;
